/**
 * Typed HTTP helpers for Widget Edge Gateway:
 * GET /v1/config/{pk}, POST /v1/sessions
 */

import { appConfig, log } from '../config/env';
import type {
  CreateSessionRequest,
  SessionResponse,
  WidgetConfig,
} from '../types/api';
import type { WidgetChannel } from '../types/bridge';
import { parseSessionResponse, parseWidgetConfig } from './apiGuards';
import { embedOriginHeaders } from './configHelpers';
import { WidgetApiError } from './errors';
import { isRecord } from './typeGuards';

export { WidgetApiError } from './errors';

function apiUrl(path: string): string {
  return `${appConfig.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

async function parseJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new WidgetApiError(
      'invalid_json',
      `Unexpected non-JSON response (${response.status})`,
      response.status,
    );
  }
}

async function readErrorBody(response: Response, body: unknown): Promise<never> {
  const record = isRecord(body) ? body : null;
  const code =
    record && typeof record.error === 'string' ? record.error : 'http_error';
  const message =
    record && typeof record.message === 'string'
      ? record.message
      : `Request failed (${response.status})`;
  throw new WidgetApiError(code, message, response.status);
}

export async function fetchWidgetConfig(
  publishableKey: string,
  signal?: AbortSignal,
): Promise<WidgetConfig> {
  const pk = encodeURIComponent(publishableKey);
  const url = apiUrl(`/v1/config/${pk}`);
  log('debug', 'GET widget config', { url });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...embedOriginHeaders(),
    },
    signal,
  });

  const body = await parseJsonBody(response);
  if (!response.ok) await readErrorBody(response, body);
  return parseWidgetConfig(body);
}

export async function createSession(
  request: CreateSessionRequest,
  signal?: AbortSignal,
): Promise<SessionResponse> {
  const url = apiUrl('/v1/sessions');
  log('debug', 'POST widget session', {
    url,
    surface: request.surface,
    hasVisitor: Boolean(request.visitor_id),
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...embedOriginHeaders(),
    },
    body: JSON.stringify(request),
    signal,
  });

  const body = await parseJsonBody(response);
  if (!response.ok) await readErrorBody(response, body);
  return parseSessionResponse(body);
}

/** In-memory session with expiry skew for proactive refresh. */
export interface ActiveSession {
  accessToken: string;
  expiresAt: number;
  sessionId: string;
  visitorId: string;
  avatarId: string;
}

const EXPIRY_SKEW_MS = 30_000;

export function sessionFromResponse(res: SessionResponse): ActiveSession {
  return {
    accessToken: res.access_token,
    expiresAt: Date.now() + Math.max(0, res.expires_in) * 1000,
    sessionId: res.session_id,
    visitorId: res.visitor_id,
    avatarId: res.avatar_id,
  };
}

export function isSessionValid(session: ActiveSession | null): boolean {
  if (!session?.accessToken) return false;
  return Date.now() < session.expiresAt - EXPIRY_SKEW_MS;
}

export interface EnsureSessionOptions {
  publishableKey: string;
  channel: WidgetChannel;
  visitorId?: string;
  current?: ActiveSession | null;
  /** Skip reuse and always POST /v1/sessions (e.g. header reset). */
  force?: boolean;
  signal?: AbortSignal;
}

export async function ensureSession(
  options: EnsureSessionOptions,
): Promise<ActiveSession> {
  const current = options.current ?? null;
  if (!options.force && current && isSessionValid(current)) {
    return current;
  }

  const res = await createSession(
    {
      publishable_key: options.publishableKey,
      visitor_id: options.visitorId || current?.visitorId,
      surface: options.channel,
    },
    options.signal,
  );

  return sessionFromResponse(res);
}
